import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  BarChart3,
  CheckCircle2,
  ClipboardList,
  Download,
  ExternalLink,
  Leaf,
  Link as LinkIcon,
  LogIn,
  LogOut,
  MessageSquareText,
  Minus,
  PackagePlus,
  Pencil,
  Plus,
  QrCode,
  Save,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Store,
  Trash2,
  UserPlus,
  Users,
  WalletCards,
  X
} from "lucide-react";
import { contributorDemoState, demoState, emptyState } from "./data";
import { clearFarmstandState, createId, loadFarmstandWorkspace, saveFarmstandWorkspace } from "./storage";
import type {
  AccountProfile,
  FarmstandState,
  FarmstandSummary,
  InventoryItem,
  Order,
  OrderStatus,
  PaymentMethod,
  RoleView
} from "./types";

type Cart = Record<string, number>;
type AuthMode = "signup" | "login";
type AuthRequestStatus = "idle" | "sending" | "verifying";
type InventoryMutationStatus = "idle" | "saving" | "deleting";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

interface AuthStartResponse {
  ok: boolean;
  phone: string;
}

interface AuthVerifyResponse {
  ok: boolean;
  account: AccountProfile;
}

interface FarmstandLoadResponse {
  account: AccountProfile;
  farmstands: FarmstandSummary[];
  farmstand: FarmstandState | null;
}

interface FarmstandMutationResponse {
  ok: boolean;
  farmstand: FarmstandState;
  order?: Order;
}

interface PublicFarmstandResponse {
  farmstand: FarmstandState;
}

class ApiRequestError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
  }
}

const TEMPORARY_AUTH_BYPASS = false;

const paymentMethods: PaymentMethod[] = ["venmo", "zelle", "paypal", "cash"];

const orderStatusLabels: Record<OrderStatus, string> = {
  pending_payment: "Pending payment",
  paid: "Paid",
  fulfilled: "Fulfilled"
};

const viewItems: Array<{
  id: RoleView;
  label: string;
  icon: typeof Store;
}> = [
  { id: "storefront", label: "Storefront", icon: Store },
  { id: "contributor", label: "Contributor", icon: PackagePlus },
  { id: "owner", label: "Owner", icon: BarChart3 }
];

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD"
});

function formatMoney(cents: number) {
  return currency.format(cents / 100);
}

function parsePriceToCents(value: string) {
  return Math.max(0, Math.round(Number(value || 0) * 100));
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(new Date(value));
}

function slugify(value: string) {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  return slug || `stand-${Math.random().toString(36).slice(2, 8)}`;
}

function createInviteCode() {
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

function normalizePhone(value: string) {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, "");

  if (trimmed.startsWith("+") && digits.length >= 10 && digits.length <= 15) {
    return `+${digits}`;
  }

  if (digits.length === 10) {
    return `+1${digits}`;
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }

  return "";
}

function getPaymentHref(method: PaymentMethod, target: string, totalCents: number) {
  const cleanTarget = target.trim();
  const total = (totalCents / 100).toFixed(2);

  if (!cleanTarget) {
    return "";
  }

  if (method === "venmo") {
    const handle = cleanTarget.replace(/^@/, "");
    return cleanTarget.startsWith("http") ? cleanTarget : `https://venmo.com/${handle}?txn=pay&amount=${total}`;
  }

  if (method === "paypal") {
    const path = cleanTarget.replace(/^https?:\/\/(www\.)?paypal\.me\//, "");
    return cleanTarget.startsWith("http") ? cleanTarget : `https://paypal.me/${path}/${total}`;
  }

  return "";
}

function getPublicUrl(slug: string) {
  if (typeof window === "undefined") {
    return `/s/${slug}`;
  }

  return `${window.location.origin}/s/${slug}`;
}

function getPublicSlug() {
  if (typeof window === "undefined") {
    return "";
  }

  const match = window.location.pathname.match(/^\/s\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

function getFarmstandAppUrl() {
  if (typeof window === "undefined") {
    return "/";
  }

  return window.location.origin;
}

function getInviteCodeFromUrl() {
  if (typeof window === "undefined") {
    return "";
  }

  return new URLSearchParams(window.location.search).get("invite")?.trim() ?? "";
}

function getInviteStandIdFromUrl() {
  if (typeof window === "undefined") {
    return "";
  }

  return new URLSearchParams(window.location.search).get("stand")?.trim() ?? "";
}

function getContributorInviteUrl(appUrl: string, inviteCode: string, standId: string) {
  const url = new URL(appUrl);
  url.searchParams.set("invite", inviteCode);
  url.searchParams.set("stand", standId);
  return url.toString();
}

function getContributorInviteMessage(standName: string, inviteCode: string, standId: string, appUrl: string) {
  const inviteUrl = getContributorInviteUrl(appUrl, inviteCode, standId);

  return [
    `You have been invited to contribute to ${standName} on Farmstand.`,
    `Invite code: ${inviteCode}`,
    `Join the stand: ${inviteUrl}`
  ].join("\n");
}

function getSmsHref(phone: string, message: string) {
  return `sms:${phone}?body=${encodeURIComponent(message)}`;
}

function summarizeFarmstand(farmstand: FarmstandState): FarmstandSummary {
  return {
    id: farmstand.id,
    name: farmstand.settings.name,
    slug: farmstand.settings.slug,
    role: farmstand.access?.role ?? "contributor"
  };
}

function upsertFarmstandSummary(farmstands: FarmstandSummary[], farmstand: FarmstandState) {
  const summary = summarizeFarmstand(farmstand);
  const existingIndex = farmstands.findIndex((candidate) => candidate.id === summary.id);

  if (existingIndex === -1) {
    return [...farmstands, summary];
  }

  return farmstands.map((candidate) => (candidate.id === summary.id ? summary : candidate));
}

function getScopedFarmstandUrl(path: string, standId: string) {
  return `/api/farmstand${path}?standId=${encodeURIComponent(standId)}`;
}

function buildOrdersCsv(orders: Order[]) {
  const rows = [
    ["Order ID", "Created", "Shopper", "Email", "Phone", "Status", "Payment Method", "Total", "Items"].join(",")
  ];

  orders.forEach((order) => {
    const values = [
      order.id,
      order.createdAt,
      order.shopperName,
      order.shopperEmail,
      order.shopperPhone,
      orderStatusLabels[order.status],
      order.paymentMethod,
      (order.totalCents / 100).toFixed(2),
      order.items.map((item) => `${item.quantity} ${item.name}`).join("; ")
    ];

    rows.push(values.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(","));
  });

  return rows.join("\n");
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify(body)
  });
  return parseJsonResponse<T>(response);
}

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  return parseJsonResponse<T>(response);
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type") ?? "";
  const data = contentType.includes("application/json") ? ((await response.json()) as { error?: string }) : null;

  if (!data) {
    throw new Error("API response was not JSON.");
  }

  if (!response.ok) {
    throw new ApiRequestError(response.status, data?.error ?? "Request failed.");
  }

  return data as T;
}

function App() {
  const [publicSlug] = useState(() => getPublicSlug());
  const [initialWorkspace] = useState(() =>
    import.meta.env.DEV
      ? loadFarmstandWorkspace()
      : {
          account: null,
          farmstands: [] as FarmstandSummary[],
          activeStandId: null,
          farmstandsById: {} as Record<string, FarmstandState>
        }
  );
  const initialFarmstand =
    !publicSlug && initialWorkspace.activeStandId
      ? initialWorkspace.farmstandsById[initialWorkspace.activeStandId]
      : null;
  const [state, setState] = useState<FarmstandState>(
    () => initialFarmstand ?? { ...emptyState, account: initialWorkspace.account }
  );
  const [farmstands, setFarmstands] = useState<FarmstandSummary[]>(() => initialWorkspace.farmstands);
  const [activeStandId, setActiveStandId] = useState<string | null>(() =>
    initialFarmstand ? initialFarmstand.id : null
  );
  const activeStandIdRef = useRef<string | null>(initialFarmstand?.id ?? null);
  const standLoadRequestRef = useRef(0);
  const devFarmstandsById = useRef<Record<string, FarmstandState>>(initialWorkspace.farmstandsById);
  const [view, setView] = useState<RoleView>(() => initialFarmstand?.access?.role ?? "storefront");
  const [syncError, setSyncError] = useState("");
  const [standNotice, setStandNotice] = useState("");
  const [standSwitching, setStandSwitching] = useState(false);
  const [cart, setCart] = useState<Cart>({});
  const [search, setSearch] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("venmo");
  const [confirmation, setConfirmation] = useState<Order | null>(null);
  const [copied, setCopied] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [authMode, setAuthMode] = useState<AuthMode>("signup");
  const [phoneAuth, setPhoneAuth] = useState({
    phone: "",
    code: "",
    codeSent: false,
    verified: false
  });
  const [authProfile, setAuthProfile] = useState({
    name: "",
    email: ""
  });
  const [authRequestStatus, setAuthRequestStatus] = useState<AuthRequestStatus>("idle");
  const [authError, setAuthError] = useState("");
  const [invitePhone, setInvitePhone] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [inviteNotice, setInviteNotice] = useState("");
  const [joinInviteCode, setJoinInviteCode] = useState(() => getInviteCodeFromUrl());
  const [joinInviteStandId, setJoinInviteStandId] = useState(() => getInviteStandIdFromUrl());
  const [joinError, setJoinError] = useState("");
  const [copiedInvitationId, setCopiedInvitationId] = useState<string | null>(null);
  const [shopper, setShopper] = useState({
    name: "",
    email: "",
    phone: ""
  });
  const [setup, setSetup] = useState({
    standName: "",
    ownerName: "",
    email: "",
    phone: "",
    venmo: "",
    primaryColor: "#2f6b4f"
  });
  const [contribution, setContribution] = useState({
    itemName: "",
    category: "Produce",
    price: "",
    quantity: "1",
    unit: "each"
  });
  const [editingInventoryItem, setEditingInventoryItem] = useState<InventoryItem | null>(null);
  const [removingInventoryItem, setRemovingInventoryItem] = useState<InventoryItem | null>(null);
  const [inventoryDraft, setInventoryDraft] = useState({
    name: "",
    category: "",
    price: "",
    quantity: "0",
    unit: ""
  });
  const [inventoryMutationStatus, setInventoryMutationStatus] = useState<InventoryMutationStatus>("idle");
  const [inventoryMutationError, setInventoryMutationError] = useState("");

  useEffect(() => {
    if (import.meta.env.DEV) {
      if (state.id) {
        devFarmstandsById.current = {
          ...devFarmstandsById.current,
          [state.id]: state
        };
      }

      saveFarmstandWorkspace({
        account: state.account,
        farmstands,
        activeStandId,
        farmstandsById: devFarmstandsById.current
      });
    } else {
      clearFarmstandState();
    }
  }, [activeStandId, farmstands, state]);

  useEffect(() => {
    setContribution({
      itemName: "",
      category: "Produce",
      price: "",
      quantity: "1",
      unit: "each"
    });
  }, [state.access?.memberId]);

  useEffect(() => {
    let cancelled = false;

    async function loadAppData() {
      try {
        if (publicSlug) {
          const result = await getJson<PublicFarmstandResponse>(`/api/public/stands/${encodeURIComponent(publicSlug)}`);

          if (!cancelled) {
            setState(result.farmstand);
            setView("storefront");
          }

          return;
        }

        const result = await getJson<FarmstandLoadResponse>("/api/farmstand");

        if (cancelled) {
          return;
        }

        setFarmstands(result.farmstands);

        const accountState = {
          ...emptyState,
          account: result.account
        };

        setState(accountState);
        activeStandIdRef.current = null;
        setActiveStandId(null);

        setSetup((current) => ({
          ...current,
          ownerName: current.ownerName || result.account.name || "",
          email: current.email || result.account.email || "",
          phone: current.phone || result.account.phone || ""
        }));
      } catch (error) {
        if (!import.meta.env.DEV && error instanceof ApiRequestError && error.status === 401) {
          setState(emptyState);
          setFarmstands([]);
          activeStandIdRef.current = null;
          setActiveStandId(null);
          setView("storefront");
        }
      }
    }

    loadAppData();

    return () => {
      cancelled = true;
    };
  }, [publicSlug]);

  useEffect(() => {
    const handleInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
  }, []);

  useEffect(() => {
    if (!editingInventoryItem && !removingInventoryItem) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const closeDialog = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || inventoryMutationStatus !== "idle") {
        return;
      }

      setEditingInventoryItem(null);
      setRemovingInventoryItem(null);
      setInventoryMutationError("");
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeDialog);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeDialog);
    };
  }, [editingInventoryItem, inventoryMutationStatus, removingInventoryItem]);

  const publicUrl = useMemo(() => getPublicUrl(state.settings.slug), [state.settings.slug]);
  const farmstandAppUrl = useMemo(() => getFarmstandAppUrl(), []);
  const contributorInviteMessage = useMemo(
    () => getContributorInviteMessage(state.settings.name, state.settings.inviteCode, state.id, farmstandAppUrl),
    [farmstandAppUrl, state.id, state.settings.inviteCode, state.settings.name]
  );
  const ownedFarmstands = useMemo(
    () => farmstands.filter((farmstand) => farmstand.role === "owner"),
    [farmstands]
  );
  const contributedFarmstands = useMemo(
    () => farmstands.filter((farmstand) => farmstand.role === "contributor"),
    [farmstands]
  );

  const availableInventory = useMemo(() => {
    const query = search.trim().toLowerCase();

    return state.inventory.filter((item) => {
      const isAvailable = item.quantity > 0;
      const matches =
        !query ||
        item.name.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query) ||
        item.contributorName.toLowerCase().includes(query);

      return isAvailable && matches;
    });
  }, [search, state.inventory]);

  const contributorInventory = useMemo(
    () => state.inventory.filter((item) => item.contributorId === state.access?.memberId),
    [state.access?.memberId, state.inventory]
  );
  const inventoryNames = useMemo(
    () => Array.from(new Set(contributorInventory.map((item) => item.name))).sort(),
    [contributorInventory]
  );

  const cartLines = useMemo(() => {
    return Object.entries(cart)
      .map(([itemId, quantity]) => {
        const item = state.inventory.find((candidate) => candidate.id === itemId);
        if (!item || quantity <= 0) {
          return null;
        }

        return {
          item,
          quantity,
          lineTotal: item.priceCents * quantity
        };
      })
      .filter(Boolean) as Array<{ item: InventoryItem; quantity: number; lineTotal: number }>;
  }, [cart, state.inventory]);

  const cartTotal = cartLines.reduce((sum, line) => sum + line.lineTotal, 0);
  const cartCount = cartLines.reduce((sum, line) => sum + line.quantity, 0);
  const selectedPaymentTarget = state.settings.paymentMethods[paymentMethod];
  const selectedPaymentHref = getPaymentHref(paymentMethod, selectedPaymentTarget, cartTotal);

  const inventoryValue = state.inventory.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);
  const salesTotal = state.orders.reduce((sum, order) => sum + order.totalCents, 0);
  const openOrders = state.orders.filter((order) => order.status !== "fulfilled").length;
  const contributorInventoryValue = contributorInventory.reduce(
    (sum, item) => sum + item.priceCents * item.quantity,
    0
  );
  const contributorAvailableUnits = contributorInventory.reduce((sum, item) => sum + item.quantity, 0);
  const contributorUnitsSold = state.contributorSales.reduce((sum, sale) => sum + sale.quantity, 0);
  const contributorSalesTotal = state.contributorSales.reduce((sum, sale) => sum + sale.totalCents, 0);
  const availableViewItems =
    state.access?.role === "owner" ? viewItems : viewItems.filter((item) => item.id !== "owner");
  const shoppers = useMemo(() => {
    const contacts = new Map<string, Order>();

    state.orders.forEach((order) => {
      const key = order.shopperEmail || order.shopperPhone || order.shopperName;
      if (key && order.shopperName !== "Guest shopper") {
        contacts.set(key, order);
      }
    });

    return Array.from(contacts.values());
  }, [state.orders]);

  const appStyle = {
    "--brand": state.settings.brand.primaryColor
  } as React.CSSProperties & { "--brand": string };
  const isPublicStorefront = Boolean(publicSlug);
  const hasContributorInvite = Boolean(joinInviteCode.trim());
  const canSendPhoneCode = Boolean(normalizePhone(phoneAuth.phone));
  const authIsBusy = authRequestStatus !== "idle";
  const canSendAuthCode =
    canSendPhoneCode &&
    (authMode === "login" || (authProfile.name.trim().length > 0 && authProfile.email.trim().length > 0));
  const canVerifyPhoneCode = phoneAuth.code.trim().length >= 4;

  function updateState(updater: (previous: FarmstandState) => FarmstandState) {
    setState((previous) => updater(previous));
  }

  function applyFarmstand(nextFarmstand: FarmstandState, nextView?: RoleView, forceActivation = false) {
    const currentActiveStandId = activeStandIdRef.current;

    setFarmstands((current) => upsertFarmstandSummary(current, nextFarmstand));
    if (import.meta.env.DEV) {
      devFarmstandsById.current = {
        ...devFarmstandsById.current,
        [nextFarmstand.id]: nextFarmstand
      };
    }

    if (!forceActivation && nextFarmstand.id !== currentActiveStandId) {
      return;
    }

    const isStandSwitch = nextFarmstand.id !== currentActiveStandId;

    if (isStandSwitch) {
      resetStandTransientState();
    }

    setSyncError("");
    setState(nextFarmstand);
    activeStandIdRef.current = nextFarmstand.id;
    setActiveStandId(nextFarmstand.id);

    if (nextView) {
      setView(nextView);
    } else if (isStandSwitch) {
      setView(nextFarmstand.access?.role ?? "storefront");
    }
  }

  function resetStandTransientState() {
    setCart({});
    setSearch("");
    setConfirmation(null);
    setCopied(false);
    setInvitePhone("");
    setInviteError("");
    setInviteNotice("");
    setCopiedInvitationId(null);
    setShopper({ name: "", email: "", phone: "" });
    setContribution({
      itemName: "",
      category: "Produce",
      price: "",
      quantity: "1",
      unit: "each"
    });
    setEditingInventoryItem(null);
    setRemovingInventoryItem(null);
    setInventoryDraft({ name: "", category: "", price: "", quantity: "0", unit: "" });
    setInventoryMutationStatus("idle");
    setInventoryMutationError("");
    setStandNotice("");
  }

  async function openFarmstand(standId: string) {
    if (!standId || (standId === activeStandId && state.id === standId)) {
      return;
    }

    setStandSwitching(true);
    setSyncError("");
    setStandNotice("");
    const requestId = ++standLoadRequestRef.current;

    try {
      const cachedFarmstand = import.meta.env.DEV ? devFarmstandsById.current[standId] : null;

      if (cachedFarmstand) {
        if (requestId === standLoadRequestRef.current) {
          applyFarmstand(cachedFarmstand, cachedFarmstand.access?.role ?? "storefront", true);
        }
        return;
      }

      const result = await getJson<FarmstandLoadResponse>(
        `/api/farmstand?standId=${encodeURIComponent(standId)}`
      );

      if (!result.farmstand) {
        throw new Error("That farmstand is no longer available.");
      }

      if (requestId !== standLoadRequestRef.current) {
        return;
      }

      setFarmstands(result.farmstands);
      applyFarmstand(result.farmstand, result.farmstand.access?.role ?? "storefront", true);
    } catch (error) {
      if (requestId === standLoadRequestRef.current) {
        setSyncError(error instanceof Error ? error.message : "Could not open that farmstand.");
      }
    } finally {
      if (requestId === standLoadRequestRef.current) {
        setStandSwitching(false);
      }
    }
  }

  function openFarmstandHub() {
    if (state.id && import.meta.env.DEV) {
      devFarmstandsById.current = {
        ...devFarmstandsById.current,
        [state.id]: state
      };
    }

    const account = state.account;
    standLoadRequestRef.current += 1;
    activeStandIdRef.current = null;
    resetStandTransientState();
    setState({ ...emptyState, account });
    setActiveStandId(null);
    setView("storefront");
    setSyncError("");
  }

  async function persistSettings(nextSettings: FarmstandState["settings"]) {
    if (!state.account || !state.id || isPublicStorefront) {
      return;
    }

    try {
      const response = await fetch(getScopedFarmstandUrl("", state.id), {
        method: "PATCH",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({ settings: nextSettings })
      });
      const result = await parseJsonResponse<FarmstandMutationResponse>(response);
      applyFarmstand(result.farmstand);
    } catch (error) {
      if (!import.meta.env.DEV) {
        setSyncError(error instanceof Error ? error.message : "Could not save stand settings.");
      }
    }
  }

  function switchAuthMode(mode: AuthMode) {
    setAuthMode(mode);
    setAuthError("");
    setPhoneAuth((current) => ({
      ...current,
      code: "",
      codeSent: false,
      verified: false
    }));
  }

  async function handleSendPhoneCode(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!canSendAuthCode || authIsBusy) {
      return;
    }

    setAuthError("");

    if (TEMPORARY_AUTH_BYPASS) {
      applyVerifiedAccount(buildTemporaryAccount());
      return;
    }

    setAuthRequestStatus("sending");

    try {
      await postJson<AuthStartResponse>("/api/auth/start", {
        mode: authMode,
        name: authProfile.name.trim(),
        email: authProfile.email.trim(),
        phone: phoneAuth.phone.trim()
      });
      setPhoneAuth((current) => ({
        ...current,
        codeSent: true,
        verified: false
      }));
    } catch (error) {
      if (import.meta.env.DEV) {
        setPhoneAuth((current) => ({
          ...current,
          codeSent: true,
          verified: false
        }));
        return;
      }

      setAuthError(error instanceof Error ? error.message : "Could not send the verification code.");
    } finally {
      setAuthRequestStatus("idle");
    }
  }

  async function handleVerifyPhoneCode(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!canVerifyPhoneCode || authIsBusy) {
      return;
    }

    setAuthError("");

    if (TEMPORARY_AUTH_BYPASS) {
      applyVerifiedAccount(buildTemporaryAccount());
      return;
    }

    setAuthRequestStatus("verifying");

    try {
      const result = await postJson<AuthVerifyResponse>("/api/auth/verify", {
        mode: authMode,
        name: authProfile.name.trim(),
        email: authProfile.email.trim(),
        phone: phoneAuth.phone.trim(),
        code: phoneAuth.code.trim()
      });
      applyVerifiedAccount(result.account);
      await loadAuthenticatedFarmstand(result.account);
    } catch (error) {
      if (import.meta.env.DEV) {
        applyVerifiedAccount({
          name: authMode === "signup" ? authProfile.name.trim() : authProfile.name.trim() || "Farmstand owner",
          email: authProfile.email.trim(),
          phone: phoneAuth.phone.trim()
        });
        return;
      }

      setAuthError(error instanceof Error ? error.message : "Could not verify that code.");
    } finally {
      setAuthRequestStatus("idle");
    }
  }

  function applyVerifiedAccount(nextAccount: AccountProfile) {
    setPhoneAuth((current) => ({
      ...current,
      verified: true
    }));
    setState((previous) => ({
      ...previous,
      account: nextAccount
    }));
    setSetup((current) => ({
      ...current,
      ownerName: current.ownerName || nextAccount.name,
      email: current.email || nextAccount.email,
      phone: current.phone || nextAccount.phone
    }));
  }

  async function loadAuthenticatedFarmstand(account: AccountProfile) {
    try {
      const result = await getJson<FarmstandLoadResponse>("/api/farmstand");
      setFarmstands(result.farmstands);

      setState({
        ...emptyState,
        account
      });
      activeStandIdRef.current = null;
      setActiveStandId(null);
      setView("storefront");
      return result.farmstands.length > 0;
    } catch (error) {
      if (!import.meta.env.DEV) {
        setSyncError(error instanceof Error ? error.message : "Could not load your stands.");
      }
      return false;
    }
  }

  function buildTemporaryAccount(): AccountProfile {
    return {
      name: authMode === "signup" ? authProfile.name.trim() : authProfile.name.trim() || "Farmstand owner",
      email: authProfile.email.trim(),
      phone: phoneAuth.phone.trim()
    };
  }

  async function handleSetupSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const standName = setup.standName.trim();
    const ownerName = setup.ownerName.trim();

    if (!standName || !ownerName) {
      return;
    }

    const ownerId = createId("contributor");
    const nextState: FarmstandState = {
      id: createId("stand"),
      account: state.account,
      access: {
        role: "owner",
        memberId: ownerId,
        memberName: ownerName
      },
      settings: {
        name: standName,
        slug: slugify(standName),
        ownerName,
        inviteCode: createInviteCode(),
        paymentMethods: {
          venmo: setup.venmo.trim(),
          zelle: "",
          paypal: "",
          cash: ""
        },
        brand: {
          primaryColor: setup.primaryColor,
          logoUrl: "",
          font: "Inter"
        }
      },
      contributors: [
        {
          id: ownerId,
          name: ownerName,
          email: setup.email.trim(),
          phone: setup.phone.trim() || phoneAuth.phone.trim(),
          role: "owner"
        }
      ],
      invitations: [],
      inventory: [],
      orders: [],
      contributorSales: []
    };

    if (state.account) {
      try {
        const result = await postJson<FarmstandMutationResponse>("/api/farmstand", {
          settings: nextState.settings,
          ownerEmail: setup.email.trim(),
          ownerPhone: setup.phone.trim() || phoneAuth.phone.trim()
        });
        applyFarmstand(result.farmstand, "owner", true);
        setView("owner");
        resetNewStandForm();
        return;
      } catch (error) {
        if (!import.meta.env.DEV) {
          setSyncError(error instanceof Error ? error.message : "Could not create the stand.");
          return;
        }
      }
    }

    applyFarmstand(nextState, "owner", true);
    resetNewStandForm();
  }

  async function handleJoinStand(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await joinStand(joinInviteCode);
  }

  async function joinStand(value: string, invitedStandId = "") {
    setJoinError("");

    const inviteCode = value.trim();

    if (!inviteCode) {
      setJoinError("Enter the invite code from the owner.");
      return false;
    }

    setStandSwitching(true);

    try {
      const result = await postJson<FarmstandMutationResponse>("/api/farmstand/join", {
        inviteCode,
        ...(invitedStandId ? { standId: invitedStandId } : {})
      });
      applyFarmstand(result.farmstand, "contributor", true);
      setStandNotice(`You joined ${result.farmstand.settings.name} as a contributor.`);
      setJoinInviteCode("");
      setJoinInviteStandId("");
      clearInviteFromUrl();
      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        setJoinError("Run through Cloudflare Pages Functions to join a real stand.");
        return false;
      }

      setJoinError(error instanceof Error ? error.message : "Could not join that stand.");
      return false;
    } finally {
      setStandSwitching(false);
    }
  }

  function clearInviteFromUrl() {
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.delete("invite");
    nextUrl.searchParams.delete("stand");
    window.history.replaceState({}, "", nextUrl);
  }

  function dismissJoinInvite() {
    setJoinInviteCode("");
    setJoinInviteStandId("");
    setJoinError("");
    clearInviteFromUrl();
  }

  function resetNewStandForm() {
    setSetup((current) => ({
      ...current,
      standName: "",
      venmo: "",
      primaryColor: "#2f6b4f"
    }));
  }

  function loadDemoStand() {
    const account = state.account ?? demoState.account;
    const ownerDemo = { ...demoState, account };
    const contributorDemo = { ...contributorDemoState, account };

    devFarmstandsById.current = {
      ...devFarmstandsById.current,
      [ownerDemo.id]: ownerDemo,
      [contributorDemo.id]: contributorDemo
    };
    setFarmstands((current) => [
      summarizeFarmstand(ownerDemo),
      summarizeFarmstand(contributorDemo),
      ...current.filter((farmstand) => farmstand.id !== ownerDemo.id && farmstand.id !== contributorDemo.id)
    ]);
    applyFarmstand(ownerDemo, "owner", true);
  }

  function addToCart(item: InventoryItem) {
    setConfirmation(null);
    setCart((current) => {
      const currentQuantity = current[item.id] ?? 0;
      const nextQuantity = Math.min(item.quantity, currentQuantity + 1);
      return { ...current, [item.id]: nextQuantity };
    });
  }

  function decrementCartItem(itemId: string) {
    setCart((current) => {
      const nextQuantity = (current[itemId] ?? 0) - 1;
      if (nextQuantity <= 0) {
        const { [itemId]: _removed, ...rest } = current;
        return rest;
      }

      return { ...current, [itemId]: nextQuantity };
    });
  }

  async function handleCheckout() {
    if (!cartLines.length) {
      return;
    }

    try {
      const orderUrl =
        state.account && !isPublicStorefront && state.id
          ? getScopedFarmstandUrl("/orders", state.id)
          : "/api/farmstand/orders";
      const result = await postJson<FarmstandMutationResponse>(orderUrl, {
        slug: publicSlug || state.settings.slug,
        shopperName: shopper.name.trim(),
        shopperEmail: shopper.email.trim(),
        shopperPhone: shopper.phone.trim(),
        paymentMethod,
        items: cartLines.map(({ item, quantity }) => ({
          itemId: item.id,
          quantity
        }))
      });

      applyFarmstand(result.farmstand);
      setCart({});
      setConfirmation(result.order ?? null);
      return;
    } catch (error) {
      if (!import.meta.env.DEV) {
        setSyncError(error instanceof Error ? error.message : "Could not place the order.");
        return;
      }
    }

    const now = new Date().toISOString();
    const order: Order = {
      id: createId("order"),
      shopperName: shopper.name.trim() || "Guest shopper",
      shopperEmail: shopper.email.trim(),
      shopperPhone: shopper.phone.trim(),
      paymentMethod,
      status: "pending_payment",
      totalCents: cartTotal,
      createdAt: now,
      items: cartLines.map(({ item, quantity }) => ({
        itemId: item.id,
        name: item.name,
        contributorId: item.contributorId,
        contributorName: item.contributorName,
        quantity,
        priceCents: item.priceCents
      }))
    };

    updateState((previous) => ({
      ...previous,
      inventory: previous.inventory.map((item) => {
        const quantityPurchased = cart[item.id] ?? 0;
        return quantityPurchased
          ? { ...item, quantity: Math.max(0, item.quantity - quantityPurchased), updatedAt: now }
          : item;
      }),
      orders: [order, ...previous.orders]
    }));

    setCart({});
    setConfirmation(order);
  }

  async function handleContributionSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const itemName = contribution.itemName.trim();
    const contributorId = state.access?.memberId ?? "";
    const contributorName = state.access?.memberName ?? state.account?.name ?? "";
    const quantity = Math.max(0, Number.parseInt(contribution.quantity, 10) || 0);
    const priceCents = parsePriceToCents(contribution.price);

    if (!contributorId || !contributorName) {
      setSyncError("Your contributor membership could not be loaded. Refresh and try again.");
      return;
    }

    if (!itemName || quantity <= 0 || priceCents <= 0) {
      return;
    }

    setSyncError("");

    if (state.account && !isPublicStorefront) {
      try {
        const result = await postJson<FarmstandMutationResponse>(
          getScopedFarmstandUrl("/inventory", state.id),
          {
          itemName,
          category: contribution.category.trim() || "General",
          priceCents,
          quantity,
          unit: contribution.unit.trim() || "each"
          }
        );
        applyFarmstand(result.farmstand);
        setContribution((current) => ({
          ...current,
          itemName: "",
          price: "",
          quantity: "1"
        }));
        return;
      } catch (error) {
        if (!import.meta.env.DEV) {
          setSyncError(error instanceof Error ? error.message : "Could not save inventory.");
          return;
        }
      }
    }

    const now = new Date().toISOString();

    updateState((previous) => {
      const existingContributor = previous.contributors.find((contributor) => contributor.id === contributorId);
      const contributor =
        existingContributor ??
        ({
          id: contributorId,
          name: contributorName,
          email: "",
          phone: "",
          role: previous.access?.role ?? "contributor"
        } satisfies FarmstandState["contributors"][number]);
      const existingItem = previous.inventory.find(
        (item) =>
          item.contributorId === contributor.id &&
          item.name.toLowerCase() === itemName.toLowerCase() &&
          item.unit.toLowerCase() === contribution.unit.trim().toLowerCase()
      );

      const contributors = existingContributor ? previous.contributors : [...previous.contributors, contributor];
      const inventory = existingItem
        ? previous.inventory.map((item) =>
            item.id === existingItem.id
              ? {
                  ...item,
                  category: contribution.category.trim() || item.category,
                  priceCents,
                  quantity: item.quantity + quantity,
                  updatedAt: now
                }
              : item
          )
        : [
            {
              id: createId("item"),
              name: itemName,
              contributorId: contributor.id,
              contributorName: contributor.name,
              category: contribution.category.trim() || "General",
              priceCents,
              quantity,
              unit: contribution.unit.trim() || "each",
              updatedAt: now
            },
            ...previous.inventory
          ];

      return { ...previous, contributors, inventory };
    });

    setContribution((current) => ({
      ...current,
      itemName: "",
      price: "",
      quantity: "1"
    }));
  }

  function updateContributionItemName(itemName: string) {
    const previousItem = contributorInventory.find(
      (item) => item.name.toLowerCase() === itemName.trim().toLowerCase()
    );

    setContribution((current) =>
      previousItem
        ? {
            ...current,
            itemName,
            category: previousItem.category,
            price: (previousItem.priceCents / 100).toFixed(2),
            unit: previousItem.unit
          }
        : { ...current, itemName }
    );
  }

  function openInventoryEditor(item: InventoryItem) {
    setRemovingInventoryItem(null);
    setEditingInventoryItem(item);
    setInventoryDraft({
      name: item.name,
      category: item.category,
      price: (item.priceCents / 100).toFixed(2),
      quantity: String(item.quantity),
      unit: item.unit
    });
    setInventoryMutationError("");
  }

  function openInventoryRemoval(item: InventoryItem) {
    setEditingInventoryItem(null);
    setRemovingInventoryItem(item);
    setInventoryMutationError("");
  }

  function closeInventoryDialog() {
    if (inventoryMutationStatus !== "idle") {
      return;
    }

    setEditingInventoryItem(null);
    setRemovingInventoryItem(null);
    setInventoryMutationError("");
  }

  async function handleInventoryEditSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!editingInventoryItem || inventoryMutationStatus !== "idle") {
      return;
    }

    const name = inventoryDraft.name.trim();
    const category = inventoryDraft.category.trim() || "General";
    const priceCents = parsePriceToCents(inventoryDraft.price);
    const quantity = Number(inventoryDraft.quantity);
    const unit = inventoryDraft.unit.trim();

    if (!name || !unit || !Number.isFinite(priceCents) || priceCents <= 0) {
      setInventoryMutationError("Enter a name, unit, and price greater than zero.");
      return;
    }

    if (!Number.isInteger(quantity) || quantity < 0) {
      setInventoryMutationError("Available quantity must be a whole number of zero or more.");
      return;
    }

    setInventoryMutationStatus("saving");
    setInventoryMutationError("");

    if (state.account && !isPublicStorefront) {
      try {
        const response = await fetch(
          getScopedFarmstandUrl(`/inventory/${encodeURIComponent(editingInventoryItem.id)}`, state.id),
          {
            method: "PATCH",
            headers: {
              "content-type": "application/json"
            },
            body: JSON.stringify({ name, category, priceCents, quantity, unit })
          }
        );
        const result = await parseJsonResponse<FarmstandMutationResponse>(response);
        applyFarmstand(result.farmstand);
        setEditingInventoryItem(null);
        setInventoryMutationStatus("idle");
        return;
      } catch (error) {
        if (!import.meta.env.DEV) {
          setInventoryMutationError(error instanceof Error ? error.message : "Could not update this item.");
          setInventoryMutationStatus("idle");
          return;
        }
      }
    }

    const updatedAt = new Date().toISOString();
    updateState((previous) => ({
      ...previous,
      inventory: previous.inventory.map((item) =>
        item.id === editingInventoryItem.id
          ? { ...item, name, category, priceCents, quantity, unit, updatedAt }
          : item
      )
    }));
    setEditingInventoryItem(null);
    setInventoryMutationStatus("idle");
  }

  async function removeInventoryItem() {
    if (!removingInventoryItem || inventoryMutationStatus !== "idle") {
      return;
    }

    setInventoryMutationStatus("deleting");
    setInventoryMutationError("");

    if (state.account && !isPublicStorefront) {
      try {
        const response = await fetch(
          getScopedFarmstandUrl(`/inventory/${encodeURIComponent(removingInventoryItem.id)}`, state.id),
          {
            method: "DELETE"
          }
        );
        const result = await parseJsonResponse<FarmstandMutationResponse>(response);
        applyFarmstand(result.farmstand);
        setRemovingInventoryItem(null);
        setInventoryMutationStatus("idle");
        setCart((current) => {
          const { [removingInventoryItem.id]: _removed, ...rest } = current;
          return rest;
        });
        return;
      } catch (error) {
        if (!import.meta.env.DEV) {
          setInventoryMutationError(error instanceof Error ? error.message : "Could not remove this item.");
          setInventoryMutationStatus("idle");
          return;
        }
      }
    }

    updateState((previous) => ({
      ...previous,
      inventory: previous.inventory.filter((item) => item.id !== removingInventoryItem.id)
    }));
    setCart((current) => {
      const { [removingInventoryItem.id]: _removed, ...rest } = current;
      return rest;
    });
    setRemovingInventoryItem(null);
    setInventoryMutationStatus("idle");
  }

  async function handleContributorInvite(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setInviteError("");
    setInviteNotice("");

    const phone = normalizePhone(invitePhone);

    if (!phone) {
      setInviteError("Enter a valid mobile number.");
      return;
    }

    if (!state.settings.inviteCode.trim()) {
      setInviteError("Add an invite code in Stand settings before preparing a text.");
      return;
    }

    if (state.account && !isPublicStorefront) {
      try {
        const result = await postJson<FarmstandMutationResponse>(
          getScopedFarmstandUrl("/invitations", state.id),
          { phone }
        );
        applyFarmstand(result.farmstand);
        setInvitePhone("");
        setInviteNotice(`Text prepared for ${phone}. Review and send it from your Messages app.`);
        window.location.href = getSmsHref(phone, contributorInviteMessage);
        return;
      } catch (error) {
        if (!import.meta.env.DEV) {
          setInviteError(error instanceof Error ? error.message : "Could not save the invitation.");
          return;
        }
      }
    }

    const ownerName = state.settings.ownerName.trim().toLowerCase();
    const ownerPhones = [
      state.account?.phone ?? "",
      ...state.contributors
        .filter((contributor) => contributor.name.trim().toLowerCase() === ownerName)
        .map((contributor) => contributor.phone)
    ]
      .map(normalizePhone)
      .filter(Boolean);

    if (ownerPhones.includes(phone)) {
      setInviteError("Enter a phone number other than your own.");
      return;
    }

    if (state.contributors.some((contributor) => normalizePhone(contributor.phone) === phone)) {
      setInviteError("That phone number already belongs to a contributor.");
      return;
    }

    if (state.invitations.some((invitation) => normalizePhone(invitation.phone) === phone)) {
      setInviteError("An invitation for that phone number is already pending.");
      return;
    }

    const invitation: FarmstandState["invitations"][number] = {
      id: createId("invitation"),
      phone,
      status: "pending",
      createdAt: new Date().toISOString()
    };
    const nextState = {
      ...state,
      invitations: [invitation, ...state.invitations]
    };

    setState(nextState);
    if (import.meta.env.DEV) {
      devFarmstandsById.current = {
        ...devFarmstandsById.current,
        [nextState.id]: nextState
      };
      saveFarmstandWorkspace({
        account: nextState.account,
        farmstands,
        activeStandId: nextState.id,
        farmstandsById: devFarmstandsById.current
      });
    }
    setInvitePhone("");
    setInviteNotice(`Text prepared for ${phone}. Review and send it from your Messages app.`);
    window.location.href = getSmsHref(phone, contributorInviteMessage);
  }

  async function copyContributorInviteMessage(invitationId: string) {
    const invitation = state.invitations.find((candidate) => candidate.id === invitationId);

    if (!invitation) {
      return;
    }

    setInviteError("");

    try {
      if (!navigator.clipboard) {
        throw new Error("Clipboard access is unavailable.");
      }

      await navigator.clipboard.writeText(contributorInviteMessage);
      setCopiedInvitationId(invitationId);
      setInviteNotice(`Invite message copied. Paste it into a text to ${invitation.phone} and send it.`);
      window.setTimeout(() => {
        setCopiedInvitationId((current) => (current === invitationId ? null : current));
      }, 1600);
    } catch {
      setInviteError("Could not copy the invite message. Open the text message instead.");
    }
  }

  async function removeContributorInvitation(invitationId: string) {
    if (state.account && !isPublicStorefront) {
      try {
        const response = await fetch(
          getScopedFarmstandUrl(`/invitations/${encodeURIComponent(invitationId)}`, state.id),
          {
            method: "DELETE"
          }
        );
        const result = await parseJsonResponse<FarmstandMutationResponse>(response);
        applyFarmstand(result.farmstand);
        setCopiedInvitationId((current) => (current === invitationId ? null : current));
        setInviteError("");
        setInviteNotice("Pending invitation removed.");
        return;
      } catch (error) {
        if (!import.meta.env.DEV) {
          setInviteError(error instanceof Error ? error.message : "Could not remove the invitation.");
          return;
        }
      }
    }

    updateState((previous) => ({
      ...previous,
      invitations: previous.invitations.filter((invitation) => invitation.id !== invitationId)
    }));
    setCopiedInvitationId((current) => (current === invitationId ? null : current));
    setInviteError("");
    setInviteNotice("Pending invitation removed.");
  }

  function updateSettings(nextSettings: Partial<FarmstandState["settings"]>) {
    const settings = {
      ...state.settings,
      ...nextSettings
    };

    updateState((previous) => ({
      ...previous,
      settings: {
        ...previous.settings,
        ...nextSettings
      }
    }));
    void persistSettings(settings);
  }

  function updatePaymentMethod(method: PaymentMethod, value: string) {
    const settings = {
      ...state.settings,
      paymentMethods: {
        ...state.settings.paymentMethods,
        [method]: value
      }
    };

    updateState((previous) => ({
      ...previous,
      settings: {
        ...previous.settings,
        paymentMethods: {
          ...previous.settings.paymentMethods,
          [method]: value
        }
      }
    }));
    void persistSettings(settings);
  }

  function updateBrand(nextBrand: Partial<FarmstandState["settings"]["brand"]>) {
    const settings = {
      ...state.settings,
      brand: {
        ...state.settings.brand,
        ...nextBrand
      }
    };

    updateState((previous) => ({
      ...previous,
      settings: {
        ...previous.settings,
        brand: {
          ...previous.settings.brand,
          ...nextBrand
        }
      }
    }));
    void persistSettings(settings);
  }

  async function updateOrderStatus(orderId: string, status: OrderStatus) {
    if (state.account && !isPublicStorefront) {
      try {
        const response = await fetch(
          getScopedFarmstandUrl(`/orders/${encodeURIComponent(orderId)}`, state.id),
          {
            method: "PATCH",
            headers: {
              "content-type": "application/json"
            },
            body: JSON.stringify({ status })
          }
        );
        const result = await parseJsonResponse<FarmstandMutationResponse>(response);
        applyFarmstand(result.farmstand);
        return;
      } catch (error) {
        if (!import.meta.env.DEV) {
          setSyncError(error instanceof Error ? error.message : "Could not update the order.");
          return;
        }
      }
    }

    updateState((previous) => ({
      ...previous,
      orders: previous.orders.map((order) => (order.id === orderId ? { ...order, status } : order))
    }));
  }

  async function copyPublicLink() {
    await navigator.clipboard?.writeText(publicUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  function exportOrders() {
    const blob = new Blob([buildOrdersCsv(state.orders)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${state.settings.slug}-orders.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function installApp() {
    if (!installPrompt) {
      return;
    }

    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  }

  async function logOut() {
    try {
      const response = await fetch("/api/auth/logout", { method: "POST" });
      await parseJsonResponse<{ ok: boolean }>(response);
    } catch (error) {
      if (!import.meta.env.DEV) {
        setSyncError(error instanceof Error ? error.message : "Could not log out.");
        return;
      }
    }

    clearFarmstandState();
    devFarmstandsById.current = {};
    activeStandIdRef.current = null;
    standLoadRequestRef.current += 1;
    setState(emptyState);
    setFarmstands([]);
    setActiveStandId(null);
    setView("storefront");
    setStandNotice("");
    setJoinInviteCode("");
    setJoinInviteStandId("");
    setAuthError("");
    setPhoneAuth({ phone: "", code: "", codeSent: false, verified: false });
    setAuthProfile({ name: "", email: "" });
    setEditingInventoryItem(null);
    setRemovingInventoryItem(null);
    setInventoryMutationError("");
  }

  function renderJoinInvitePrompt() {
    if (!joinInviteCode.trim()) {
      return null;
    }

    return (
      <section className="workspace-panel join-invite-panel" aria-labelledby="join-invite-title">
        <div className="join-invite-copy">
          <div className="auth-icon" aria-hidden="true">
            <UserPlus size={22} />
          </div>
          <div>
            <p className="eyebrow">Farmstand invitation</p>
            <h2 id="join-invite-title">Join this stand?</h2>
            <p>Joining adds this stand to My farmstands. Your other stands will not change.</p>
          </div>
        </div>
        <div className="invite-pill" aria-label={`Invite code ${joinInviteCode}`}>
          {joinInviteCode}
        </div>
        <div className="join-invite-actions">
          <button
            className="primary-button"
            type="button"
            onClick={() => void joinStand(joinInviteCode, joinInviteStandId)}
            disabled={standSwitching}
          >
            <UserPlus size={18} />
            {standSwitching ? "Joining..." : "Join stand"}
          </button>
          <button className="ghost-button" type="button" onClick={dismissJoinInvite} disabled={standSwitching}>
            Not now
          </button>
        </div>
        {joinError ? (
          <p className="auth-alert join-invite-error" role="alert">
            {joinError}
          </p>
        ) : null}
      </section>
    );
  }

  if (!state.account && !isPublicStorefront) {
    return (
      <div className="app-shell setup-shell auth-only-shell" style={appStyle}>
        <header className="setup-header">
          <div className="brand-lockup">
            <div className="brand-mark" aria-hidden="true">
              <Leaf size={24} />
            </div>
            <div>
              <p className="eyebrow">Farmstand</p>
              <h1>Farmstand</h1>
            </div>
          </div>
          {installPrompt ? (
            <button className="primary-button" type="button" onClick={installApp}>
              <Download size={18} />
              Install
            </button>
          ) : null}
        </header>

        <main className="auth-main">
          <section className="setup-panel auth-panel" aria-label="Farmstand access">
            <div className="auth-heading">
              <div className="auth-icon">
                {authMode === "signup" ? <Sparkles size={22} /> : <LogIn size={22} />}
              </div>
              <div>
                <p className="eyebrow">{hasContributorInvite ? "Contributor invitation" : "Owner access"}</p>
                <h2>
                  {hasContributorInvite
                    ? authMode === "signup"
                      ? "Sign up to join the stand"
                      : "Log in to join the stand"
                    : authMode === "signup"
                      ? "Sign up to create a stand"
                      : "Log in to manage stands"}
                </h2>
              </div>
            </div>

            <div className="auth-mode-toggle" aria-label="Choose access mode">
              <button
                className={authMode === "signup" ? "segmented-button active" : "segmented-button"}
                type="button"
                onClick={() => switchAuthMode("signup")}
              >
                Sign up
              </button>
              <button
                className={authMode === "login" ? "segmented-button active" : "segmented-button"}
                type="button"
                onClick={() => switchAuthMode("login")}
              >
                Log in
              </button>
            </div>

            {authError ? (
              <p className="auth-alert" role="alert">
                {authError}
              </p>
            ) : null}

            {!phoneAuth.verified ? (
              <div className="phone-auth-stack">
                <form className="phone-auth-form" onSubmit={handleSendPhoneCode}>
                  {authMode === "signup" ? (
                    <>
                      <label>
                        Name
                        <input
                          value={authProfile.name}
                          onChange={(event) => {
                            const value = event.currentTarget.value;
                            setAuthProfile((current) => ({ ...current, name: value }));
                            setSetup((current) => ({ ...current, ownerName: value }));
                          }}
                          placeholder="Alex Morgan"
                          autoComplete="name"
                          required
                        />
                      </label>
                      <label>
                        Email
                        <input
                          value={authProfile.email}
                          onChange={(event) => {
                            const value = event.currentTarget.value;
                            setAuthProfile((current) => ({ ...current, email: value }));
                            setSetup((current) => ({ ...current, email: value }));
                          }}
                          placeholder="you@example.com"
                          autoComplete="email"
                          type="email"
                          required
                        />
                      </label>
                    </>
                  ) : null}
                  <label>
                    Mobile number
                    <input
                      value={phoneAuth.phone}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        setPhoneAuth((current) => ({
                          ...current,
                          phone: value,
                          codeSent: false,
                          verified: false
                        }));
                        setSetup((current) => ({ ...current, phone: value }));
                      }}
                      placeholder="(555) 010-0100"
                      autoComplete="tel"
                      inputMode="tel"
                      required
                    />
                  </label>
                  <button className="primary-button full-width" type="submit" disabled={!canSendAuthCode || authIsBusy}>
                    {TEMPORARY_AUTH_BYPASS ? <ShieldCheck size={18} /> : <MessageSquareText size={18} />}
                    {TEMPORARY_AUTH_BYPASS
                      ? authMode === "signup"
                        ? "Create account"
                        : "Log in"
                      : authRequestStatus === "sending"
                        ? "Sending..."
                        : "Text me a code"}
                  </button>
                </form>

                {!TEMPORARY_AUTH_BYPASS && phoneAuth.codeSent ? (
                  <form className="phone-auth-form" onSubmit={handleVerifyPhoneCode}>
                    <label>
                      Verification code
                      <input
                        value={phoneAuth.code}
                        onChange={(event) => {
                          const value = event.currentTarget.value;
                          setPhoneAuth((current) => ({ ...current, code: value }));
                        }}
                        placeholder="123456"
                        autoComplete="one-time-code"
                        inputMode="numeric"
                        required
                      />
                    </label>
                    <button
                      className="secondary-link-button full-width"
                      type="submit"
                      disabled={!canVerifyPhoneCode || authIsBusy}
                    >
                      <ShieldCheck size={18} />
                      {authRequestStatus === "verifying" ? "Checking..." : authMode === "signup" ? "Sign up" : "Log in"}
                    </button>
                  </form>
                ) : null}
              </div>
            ) : null}
          </section>
        </main>
      </div>
    );
  }

  if (isPublicStorefront && !state.settings.name.trim()) {
    return (
      <div className="app-shell setup-shell" style={appStyle}>
        <header className="setup-header">
          <div className="brand-lockup">
            <div className="brand-mark" aria-hidden="true">
              <Leaf size={24} />
            </div>
            <div>
              <p className="eyebrow">Farmstand</p>
              <h1>Loading stand</h1>
            </div>
          </div>
        </header>
      </div>
    );
  }

  if (!activeStandId && !isPublicStorefront) {
    return (
      <div className="app-shell setup-shell" style={appStyle}>
        <header className="setup-header">
          <div className="brand-lockup">
            <div className="brand-mark" aria-hidden="true">
              <Leaf size={24} />
            </div>
            <div>
              <p className="eyebrow">Farmstand</p>
              <h1>My farmstands</h1>
            </div>
          </div>
          <div className="hub-header-actions">
            <div className="signed-in-pill">
              <Smartphone size={16} />
              {state.account?.name || state.account?.phone}
            </div>
            <button className="icon-only-button" type="button" onClick={logOut} aria-label="Log out" title="Log out">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <main className="stand-manager-main farmstand-hub">
          {renderJoinInvitePrompt()}
          {syncError ? (
            <p className="auth-alert sync-alert" role="alert">
              {syncError}
            </p>
          ) : null}
          <section className="workspace-panel stand-list-panel" aria-labelledby="stands-title">
            <div>
              <p className="eyebrow">Your portfolio</p>
              <h2 id="stands-title">{farmstands.length ? "Choose a farmstand" : "No farmstands yet"}</h2>
              <p>
                {farmstands.length
                  ? "Choose a stand to open, create a new one, or join with an invite code."
                  : "Create your first stand or join one with an invite code."}
              </p>
            </div>

            {farmstands.length ? (
              <div className="farmstand-groups">
                {ownedFarmstands.length ? (
                  <section className="farmstand-group" aria-labelledby="owned-farmstands-heading">
                    <div className="subsection-heading">
                      <h3 id="owned-farmstands-heading">Owned by you</h3>
                      <span className="count-pill">{ownedFarmstands.length}</span>
                    </div>
                    <div className="farmstand-card-grid">
                      {ownedFarmstands.map((farmstand) => (
                        <article className="farmstand-card" key={farmstand.id}>
                          <div>
                            <Store size={20} />
                            <div>
                              <strong>{farmstand.name}</strong>
                              <span className="status-pill active">Owner</span>
                            </div>
                          </div>
                          <button
                            className="ghost-button"
                            type="button"
                            onClick={() => void openFarmstand(farmstand.id)}
                            disabled={standSwitching}
                          >
                            Open stand
                          </button>
                        </article>
                      ))}
                    </div>
                  </section>
                ) : null}

                {contributedFarmstands.length ? (
                  <section className="farmstand-group" aria-labelledby="contributed-farmstands-heading">
                    <div className="subsection-heading">
                      <h3 id="contributed-farmstands-heading">You contribute to</h3>
                      <span className="count-pill">{contributedFarmstands.length}</span>
                    </div>
                    <div className="farmstand-card-grid">
                      {contributedFarmstands.map((farmstand) => (
                        <article className="farmstand-card" key={farmstand.id}>
                          <div>
                            <PackagePlus size={20} />
                            <div>
                              <strong>{farmstand.name}</strong>
                              <span className="status-pill">Contributor</span>
                            </div>
                          </div>
                          <button
                            className="ghost-button"
                            type="button"
                            onClick={() => void openFarmstand(farmstand.id)}
                            disabled={standSwitching}
                          >
                            Open stand
                          </button>
                        </article>
                      ))}
                    </div>
                  </section>
                ) : null}
              </div>
            ) : (
              <div className="empty-state hub-empty-state">
                <Store size={28} />
                <p>No farmstands are connected to this account yet.</p>
              </div>
            )}

            {!hasContributorInvite ? (
              <form className="join-stand-form" onSubmit={handleJoinStand}>
                <div>
                  <p className="eyebrow">Have an invitation?</p>
                  <h3>Join a farmstand</h3>
                </div>
                <label>
                  Invite code
                  <input
                    value={joinInviteCode}
                    onChange={(event) => {
                      setJoinInviteCode(event.currentTarget.value);
                      setJoinInviteStandId("");
                      setJoinError("");
                    }}
                    placeholder="Invite code"
                    required
                  />
                </label>
                <p className="field-help">Use the code the owner texted to your verified phone number.</p>
                <button className="secondary-link-button full-width" type="submit" disabled={standSwitching}>
                  <UserPlus size={18} />
                  {standSwitching ? "Joining..." : "Join stand"}
                </button>
                {joinError ? (
                  <p className="auth-alert" role="alert">
                    {joinError}
                  </p>
                ) : null}
              </form>
            ) : null}
          </section>

          <section className="setup-panel" aria-label="Create a stand">
            <div className="auth-heading">
              <div className="auth-icon">
                <Store size={22} />
              </div>
              <div>
                <p className="eyebrow">New stand</p>
                <h2>Create a new stand</h2>
              </div>
            </div>
            <p className="field-help">You’ll be the owner. You can invite contributors after setup.</p>

            <form className="setup-form" onSubmit={handleSetupSubmit}>
              <label>
                Stand name
                <input
                  value={setup.standName}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setSetup((current) => ({ ...current, standName: value }));
                  }}
                  placeholder="Cedar Lane Farmstand"
                  required
                />
              </label>
              <label>
                Owner name
                <input
                  value={setup.ownerName}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setSetup((current) => ({ ...current, ownerName: value }));
                  }}
                  placeholder="Alex Morgan"
                  required
                />
              </label>
              <label>
                Email
                <input
                  value={setup.email}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setSetup((current) => ({ ...current, email: value }));
                  }}
                  placeholder="owner@example.com"
                  type="email"
                />
              </label>
              <label>
                Venmo
                <input
                  value={setup.venmo}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setSetup((current) => ({ ...current, venmo: value }));
                  }}
                  placeholder="@yourstand"
                />
              </label>
              <label>
                Primary color
                <input
                  className="color-input"
                  value={setup.primaryColor}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setSetup((current) => ({ ...current, primaryColor: value }));
                  }}
                  type="color"
                />
              </label>
              <button className="primary-button full-width" type="submit">
                <CheckCircle2 size={18} />
                Create stand
              </button>
            </form>

            {import.meta.env.DEV ? (
              <button className="ghost-button full-width" type="button" onClick={loadDemoStand}>
                <Store size={18} />
                Load sample stands
              </button>
            ) : null}
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell" style={appStyle}>
      <header className="topbar">
        <div className="brand-lockup">
          <div className="brand-mark" aria-hidden="true">
            <Leaf size={24} />
          </div>
          {state.account && !isPublicStorefront ? (
            <div className="stand-context">
              <p className="eyebrow">Active stand</p>
              <div className="stand-select-row">
                <select
                  value={activeStandId ?? ""}
                  onChange={(event) => void openFarmstand(event.currentTarget.value)}
                  aria-label="Active farmstand"
                  disabled={standSwitching}
                >
                  {ownedFarmstands.length ? (
                    <optgroup label="Owned by you">
                      {ownedFarmstands.map((farmstand) => (
                        <option key={farmstand.id} value={farmstand.id}>
                          {farmstand.name}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                  {contributedFarmstands.length ? (
                    <optgroup label="You contribute to">
                      {contributedFarmstands.map((farmstand) => (
                        <option key={farmstand.id} value={farmstand.id}>
                          {farmstand.name}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                </select>
                <span className={`status-pill stand-role-pill ${state.access?.role === "owner" ? "active" : ""}`}>
                  {state.access?.role ?? "contributor"}
                </span>
              </div>
            </div>
          ) : (
            <div>
              <p className="eyebrow">Farmstand</p>
              <h1>{state.settings.name}</h1>
            </div>
          )}
        </div>
        <div className="topbar-actions">
          {state.account && !isPublicStorefront ? (
            <button className="ghost-button" type="button" onClick={openFarmstandHub}>
              <Store size={18} />
              My farmstands
            </button>
          ) : null}
          <button className="ghost-button" type="button" onClick={copyPublicLink}>
            <LinkIcon size={18} />
            {copied ? "Copied" : "Copy link"}
          </button>
          {installPrompt ? (
            <button className="primary-button" type="button" onClick={installApp}>
              <Download size={18} />
              Install
            </button>
          ) : null}
          {state.account && !isPublicStorefront ? (
            <button className="icon-only-button" type="button" onClick={logOut} aria-label="Log out" title="Log out">
              <LogOut size={18} />
            </button>
          ) : null}
        </div>
      </header>

      {!isPublicStorefront ? (
        <nav className="role-nav" aria-label="Farmstand workspaces">
          {availableViewItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                className={view === item.id ? "nav-button active" : "nav-button"}
                key={item.id}
                type="button"
                onClick={() => setView(item.id)}
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      ) : null}

      <main className="workspace">
        {renderJoinInvitePrompt()}
        {syncError ? (
          <p className="auth-alert sync-alert" role="alert">
            {syncError}
          </p>
        ) : null}
        {standNotice ? (
          <div className="success-banner workspace-notice" role="status">
            <CheckCircle2 size={18} />
            <span>{standNotice}</span>
          </div>
        ) : null}
        {view === "storefront" ? (
          <section className="storefront-grid" aria-label="Shopper storefront">
            <div className="workspace-panel">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Shopper view</p>
                  <h2>Available today</h2>
                </div>
                <div className="search-box">
                  <label htmlFor="inventory-search">Search</label>
                  <input
                    id="inventory-search"
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    placeholder="Eggs, honey, tomatoes"
                  />
                </div>
              </div>

              <div className="product-grid">
                {availableInventory.map((item) => (
                  <article className="product-card" key={item.id}>
                    <div className="product-kicker">
                      <span>{item.category}</span>
                      <span>{item.quantity} left</span>
                    </div>
                    <h3>{item.name}</h3>
                    <p>{item.contributorName}</p>
                    <div className="product-footer">
                      <strong>
                        {formatMoney(item.priceCents)} / {item.unit}
                      </strong>
                      <button className="icon-text-button" type="button" onClick={() => addToCart(item)}>
                        <Plus size={18} />
                        Add
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <aside className="checkout-panel" aria-label="Cart">
              <div className="section-heading compact">
                <div>
                  <p className="eyebrow">Cart</p>
                  <h2>{cartCount} items</h2>
                </div>
                <ShoppingCart size={24} />
              </div>

              {cartLines.length ? (
                <div className="cart-list">
                  {cartLines.map(({ item, quantity, lineTotal }) => (
                    <div className="cart-line" key={item.id}>
                      <div>
                        <strong>{item.name}</strong>
                        <span>
                          {quantity} x {formatMoney(item.priceCents)}
                        </span>
                      </div>
                      <div className="cart-line-actions">
                        <button
                          className="icon-only-button"
                          type="button"
                          onClick={() => decrementCartItem(item.id)}
                          aria-label={`Remove one ${item.name}`}
                          title={`Remove one ${item.name}`}
                        >
                          <Minus size={16} />
                        </button>
                        <strong>{formatMoney(lineTotal)}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <ShoppingCart size={28} />
                  <p>Cart is empty.</p>
                </div>
              )}

              <div className="checkout-total">
                <span>Total</span>
                <strong>{formatMoney(cartTotal)}</strong>
              </div>

              <div className="payment-methods" aria-label="Payment method">
                {paymentMethods.map((method) => (
                  <button
                    className={paymentMethod === method ? "segmented-button active" : "segmented-button"}
                    key={method}
                    type="button"
                    onClick={() => setPaymentMethod(method)}
                  >
                    {method}
                  </button>
                ))}
              </div>

              <div className="profile-fields">
                <label>
                  Name
                  <input
                    value={shopper.name}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setShopper((current) => ({ ...current, name: value }));
                    }}
                    placeholder="Optional"
                  />
                </label>
                <label>
                  Phone
                  <input
                    value={shopper.phone}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setShopper((current) => ({ ...current, phone: value }));
                    }}
                    placeholder="Optional"
                  />
                </label>
                <label>
                  Email
                  <input
                    value={shopper.email}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setShopper((current) => ({ ...current, email: value }));
                    }}
                    placeholder="Optional"
                    type="email"
                  />
                </label>
              </div>

              <div className="payment-note">
                <WalletCards size={18} />
                <span>
                  {selectedPaymentTarget
                    ? `Pay ${formatMoney(cartTotal)} with ${paymentMethod}: ${selectedPaymentTarget}`
                    : `No ${paymentMethod} details set yet.`}
                </span>
              </div>

              <div className="checkout-actions">
                {selectedPaymentHref ? (
                  <a className="secondary-link-button" href={selectedPaymentHref} target="_blank" rel="noreferrer">
                    <ExternalLink size={18} />
                    Open {paymentMethod}
                  </a>
                ) : null}
                <button className="primary-button full-width" type="button" onClick={handleCheckout} disabled={!cartLines.length}>
                  <CheckCircle2 size={18} />
                  Place order
                </button>
              </div>

              {confirmation ? (
                <div className="success-banner" role="status">
                  <CheckCircle2 size={18} />
                  <span>Order {confirmation.id.slice(-8)} is pending payment.</span>
                </div>
              ) : null}
            </aside>
          </section>
        ) : null}

        {view === "contributor" ? (
          <section className="contributor-layout" aria-label="Contributor dashboard">
            <div className="metrics-grid">
              <Metric icon={ClipboardList} label="Inventory value" value={formatMoney(contributorInventoryValue)} />
              <Metric icon={Store} label="Units available" value={String(contributorAvailableUnits)} />
              <Metric icon={BarChart3} label="Units sold" value={String(contributorUnitsSold)} />
              <Metric icon={CheckCircle2} label="Sales attributed" value={formatMoney(contributorSalesTotal)} />
            </div>

            <div className="workspace-panel">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Contributor workspace</p>
                  <h2>Add inventory</h2>
                </div>
                <div className="member-identity">
                  <Users size={18} />
                  <div>
                    <span>Adding as</span>
                    <strong>{state.access?.memberName || state.account?.name || "Contributor"}</strong>
                  </div>
                </div>
              </div>

              <form className="entry-form contributor-entry-form" onSubmit={handleContributionSubmit} autoComplete="off">
                <label>
                  Item
                  <input
                    value={contribution.itemName}
                    onChange={(event) => updateContributionItemName(event.currentTarget.value)}
                    list="inventory-name-options"
                    placeholder="Pasture eggs"
                    required
                  />
                  <datalist id="inventory-name-options">
                    {inventoryNames.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                </label>
                <label>
                  Category
                  <input
                    value={contribution.category}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setContribution((current) => ({ ...current, category: value }));
                    }}
                  />
                </label>
                <label>
                  Price
                  <input
                    value={contribution.price}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setContribution((current) => ({ ...current, price: value }));
                    }}
                    min="0.01"
                    placeholder="6.00"
                    step="0.01"
                    type="number"
                    required
                  />
                </label>
                <label>
                  Quantity to add
                  <input
                    value={contribution.quantity}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setContribution((current) => ({ ...current, quantity: value }));
                    }}
                    min="1"
                    step="1"
                    type="number"
                    required
                  />
                </label>
                <label>
                  Unit
                  <input
                    value={contribution.unit}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setContribution((current) => ({ ...current, unit: value }));
                    }}
                    placeholder="dozen"
                    required
                  />
                </label>
                <button className="primary-button form-submit" type="submit">
                  <PackagePlus size={18} />
                  Add inventory
                </button>
              </form>
            </div>

            <div className="workspace-panel">
              <div className="section-heading compact">
                <div>
                  <p className="eyebrow">Inventory</p>
                  <h2>Your available items</h2>
                </div>
                <ClipboardList size={24} />
              </div>
              {contributorInventory.length ? (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Category</th>
                        <th>Available</th>
                        <th>Price</th>
                        <th>Updated</th>
                        <th className="table-actions-heading">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contributorInventory.map((item) => (
                        <tr key={item.id}>
                          <td className="table-primary">{item.name}</td>
                          <td>{item.category}</td>
                          <td>
                            {item.quantity} {item.unit}
                          </td>
                          <td>{formatMoney(item.priceCents)}</td>
                          <td>{formatDate(item.updatedAt)}</td>
                          <td>
                            <InventoryActions
                              item={item}
                              disabled={inventoryMutationStatus !== "idle"}
                              onEdit={openInventoryEditor}
                              onRemove={openInventoryRemoval}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state compact-empty-state">
                  <PackagePlus size={24} />
                  <p>No inventory added yet.</p>
                </div>
              )}
            </div>

            <div className="workspace-panel">
              <div className="section-heading compact">
                <div>
                  <p className="eyebrow">Sales</p>
                  <h2>Your sales breakdown</h2>
                </div>
                <BarChart3 size={24} />
              </div>
              {state.contributorSales.length ? (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Item</th>
                        <th>Quantity</th>
                        <th>Unit price</th>
                        <th>Your sales</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.contributorSales.map((sale) => (
                        <tr key={`${sale.orderId}-${sale.itemId}-${sale.itemName}`}>
                          <td>{formatDate(sale.createdAt)}</td>
                          <td className="table-primary">{sale.itemName}</td>
                          <td>{sale.quantity}</td>
                          <td>{formatMoney(sale.priceCents)}</td>
                          <td>{formatMoney(sale.totalCents)}</td>
                          <td>{orderStatusLabels[sale.status]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state compact-empty-state">
                  <BarChart3 size={24} />
                  <p>No sales attributed yet.</p>
                </div>
              )}
            </div>
          </section>
        ) : null}

        {view === "owner" && state.access?.role === "owner" ? (
          <section className="owner-layout" aria-label="Owner dashboard">
            <div className="metrics-grid">
              <Metric icon={ClipboardList} label="Inventory value" value={formatMoney(inventoryValue)} />
              <Metric icon={Store} label="Available items" value={String(state.inventory.length)} />
              <Metric icon={CheckCircle2} label="Sales tracked" value={formatMoney(salesTotal)} />
              <Metric icon={Users} label="Open orders" value={String(openOrders)} />
            </div>

            <div className="owner-grid">
              <div className="workspace-panel">
                <div className="section-heading">
                  <div>
                    <p className="eyebrow">Owner view</p>
                    <h2>Stand settings</h2>
                  </div>
                  <Settings size={24} />
                </div>

                <div className="settings-form">
                  <label>
                    Stand name
                    <input
                      value={state.settings.name}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        updateSettings({ name: value });
                      }}
                    />
                  </label>
                  <label>
                    Short link slug
                    <input
                      value={state.settings.slug}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        updateSettings({
                          slug: value
                            .trim()
                            .toLowerCase()
                            .replace(/[^a-z0-9-]/g, "-")
                        })
                      }}
                    />
                  </label>
                  <label>
                    Invite code
                    <input
                      value={state.settings.inviteCode}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        updateSettings({ inviteCode: value });
                      }}
                    />
                  </label>
                  <label>
                    Primary color
                    <input
                      className="color-input"
                      value={state.settings.brand.primaryColor}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        updateBrand({ primaryColor: value });
                      }}
                      type="color"
                    />
                  </label>
                </div>

                <div className="payment-settings">
                  {paymentMethods.map((method) => (
                    <label key={method}>
                      {method}
                      <input
                        value={state.settings.paymentMethods[method]}
                        onChange={(event) => {
                          const value = event.currentTarget.value;
                          updatePaymentMethod(method, value);
                        }}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="workspace-panel qr-panel">
                <div className="section-heading compact">
                  <div>
                    <p className="eyebrow">Shopper link</p>
                    <h2>QR access</h2>
                  </div>
                  <QrCode size={24} />
                </div>
                <div className="qr-code-frame">
                  <QRCodeSVG value={publicUrl} size={152} bgColor="transparent" fgColor="#17211c" />
                </div>
                <p className="public-url">{publicUrl}</p>
                <button className="ghost-button full-width" type="button" onClick={copyPublicLink}>
                  <LinkIcon size={18} />
                  {copied ? "Copied" : "Copy shopper link"}
                </button>
              </div>
            </div>

            <div className="workspace-panel contributor-management-panel">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Team access</p>
                  <h2>Invite contributors</h2>
                </div>
                <UserPlus size={24} />
              </div>

              <form className="contributor-invite-form" onSubmit={handleContributorInvite}>
                <label htmlFor="contributor-invite-phone">Mobile number</label>
                <div className="contributor-invite-controls">
                  <input
                    id="contributor-invite-phone"
                    value={invitePhone}
                    onChange={(event) => {
                      setInvitePhone(event.currentTarget.value);
                      setInviteError("");
                      setInviteNotice("");
                    }}
                    placeholder="(555) 010-0142"
                    autoComplete="tel"
                    inputMode="tel"
                    type="tel"
                    aria-describedby="contributor-invite-help"
                    required
                  />
                  <button className="primary-button" type="submit">
                    <MessageSquareText size={18} />
                    Open text message
                  </button>
                </div>
                <p className="field-help" id="contributor-invite-help">
                  Farmstand prepares a text with the stand name, invite code, and app link. Review and send it from
                  your Messages app; delivery is not tracked here.
                </p>
              </form>

              {inviteError ? (
                <p className="auth-alert invite-feedback" role="alert">
                  {inviteError}
                </p>
              ) : null}
              {inviteNotice ? (
                <div className="success-banner invite-feedback" role="status">
                  <CheckCircle2 size={18} />
                  <span>{inviteNotice}</span>
                </div>
              ) : null}

              <div className="contributor-management-grid">
                <section aria-labelledby="current-contributors-heading">
                  <div className="subsection-heading">
                    <h3 id="current-contributors-heading">Stand members</h3>
                    <span className="count-pill">{state.contributors.length}</span>
                  </div>
                  <div className="contributor-list">
                    {state.contributors.map((contributor) => (
                      <article className="contributor-record" key={contributor.id}>
                        <div>
                          <strong>{contributor.name}</strong>
                          <span>{contributor.phone || "No phone on file"}</span>
                        </div>
                        <span className="status-pill active">{contributor.role}</span>
                      </article>
                    ))}
                  </div>
                </section>

                <section aria-labelledby="pending-invitations-heading">
                  <div className="subsection-heading">
                    <h3 id="pending-invitations-heading">Pending invitations</h3>
                    <span className="count-pill">{state.invitations.length}</span>
                  </div>
                  {state.invitations.length ? (
                    <div className="contributor-list">
                      {state.invitations.map((invitation) => (
                        <article className="contributor-record invitation-record" key={invitation.id}>
                          <div>
                            <strong>{invitation.phone}</strong>
                            <span>Prepared {formatDate(invitation.createdAt)}</span>
                          </div>
                          <span className="status-pill">{invitation.status}</span>
                          <div className="invitation-actions">
                            <a
                              className="secondary-link-button"
                              href={getSmsHref(invitation.phone, contributorInviteMessage)}
                            >
                              <MessageSquareText size={16} />
                              Open text message
                            </a>
                            <button
                              className="ghost-button"
                              type="button"
                              onClick={() => copyContributorInviteMessage(invitation.id)}
                            >
                              {copiedInvitationId === invitation.id ? "Copied" : "Copy message"}
                            </button>
                            <button
                              className="ghost-button danger-button"
                              type="button"
                              onClick={() => removeContributorInvitation(invitation.id)}
                              aria-label={`Remove pending invitation for ${invitation.phone}`}
                            >
                              Remove
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state compact-empty-state">
                      <UserPlus size={24} />
                      <p>No pending invitations.</p>
                    </div>
                  )}
                </section>
              </div>
            </div>

            <div className="workspace-panel">
              <div className="section-heading compact">
                <div>
                  <p className="eyebrow">Inventory</p>
                  <h2>Active inventory</h2>
                </div>
                <ClipboardList size={24} />
              </div>
              {state.inventory.length ? (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Contributor</th>
                        <th>Category</th>
                        <th>Available</th>
                        <th>Price</th>
                        <th>Updated</th>
                        <th className="table-actions-heading">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.inventory.map((item) => (
                        <tr key={item.id}>
                          <td className="table-primary">{item.name}</td>
                          <td>{item.contributorName}</td>
                          <td>{item.category}</td>
                          <td>
                            {item.quantity} {item.unit}
                          </td>
                          <td>{formatMoney(item.priceCents)}</td>
                          <td>{formatDate(item.updatedAt)}</td>
                          <td>
                            <InventoryActions
                              item={item}
                              disabled={inventoryMutationStatus !== "idle"}
                              onEdit={openInventoryEditor}
                              onRemove={openInventoryRemoval}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state compact-empty-state">
                  <PackagePlus size={24} />
                  <p>No active inventory.</p>
                </div>
              )}
            </div>

            <div className="workspace-panel">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Orders</p>
                  <h2>Sales ledger</h2>
                </div>
                <button className="ghost-button" type="button" onClick={exportOrders}>
                  <Download size={18} />
                  Export CSV
                </button>
              </div>

              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Shopper</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Payment</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.orders.map((order) => (
                      <tr key={order.id}>
                        <td>{order.id.slice(-8)}</td>
                        <td>
                          <div className="table-primary">{order.shopperName}</div>
                          <span>{formatDate(order.createdAt)}</span>
                        </td>
                        <td>{order.items.map((item) => `${item.quantity} ${item.name}`).join(", ")}</td>
                        <td>{formatMoney(order.totalCents)}</td>
                        <td>{order.paymentMethod}</td>
                        <td>
                          <select
                            value={order.status}
                            onChange={(event) => {
                              const value = event.currentTarget.value as OrderStatus;
                              updateOrderStatus(order.id, value);
                            }}
                            aria-label={`Status for order ${order.id}`}
                          >
                            {Object.entries(orderStatusLabels).map(([status, label]) => (
                              <option key={status} value={status}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="workspace-panel">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Contacts</p>
                  <h2>Shopper database</h2>
                </div>
                <Users size={24} />
              </div>

              <div className="contact-grid">
                {shoppers.map((order) => (
                  <article className="contact-card" key={order.shopperEmail || order.shopperPhone || order.id}>
                    <strong>{order.shopperName}</strong>
                    <span>{order.shopperEmail || "No email"}</span>
                    <span>{order.shopperPhone || "No phone"}</span>
                  </article>
                ))}
              </div>
            </div>
          </section>
        ) : null}
      </main>

      {editingInventoryItem ? (
        <div
          className="dialog-backdrop"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) {
              closeInventoryDialog();
            }
          }}
        >
          <section
            className="inventory-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="inventory-editor-title"
          >
            <form onSubmit={handleInventoryEditSubmit}>
              <div className="dialog-heading">
                <div>
                  <p className="eyebrow">Inventory</p>
                  <h2 id="inventory-editor-title">Edit {editingInventoryItem.name}</h2>
                </div>
                <button
                  className="icon-only-button"
                  type="button"
                  onClick={closeInventoryDialog}
                  disabled={inventoryMutationStatus !== "idle"}
                  aria-label="Close inventory editor"
                  title="Close"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="inventory-edit-grid">
                <label>
                  Item
                  <input
                    value={inventoryDraft.name}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setInventoryDraft((current) => ({ ...current, name: value }));
                    }}
                    autoFocus
                    required
                  />
                </label>
                <label>
                  Category
                  <input
                    value={inventoryDraft.category}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setInventoryDraft((current) => ({ ...current, category: value }));
                    }}
                  />
                </label>
                <label>
                  Price
                  <input
                    value={inventoryDraft.price}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setInventoryDraft((current) => ({ ...current, price: value }));
                    }}
                    min="0.01"
                    step="0.01"
                    type="number"
                    required
                  />
                </label>
                <label>
                  Available quantity
                  <input
                    value={inventoryDraft.quantity}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setInventoryDraft((current) => ({ ...current, quantity: value }));
                    }}
                    min="0"
                    step="1"
                    type="number"
                    required
                  />
                </label>
                <label>
                  Unit
                  <input
                    value={inventoryDraft.unit}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setInventoryDraft((current) => ({ ...current, unit: value }));
                    }}
                    required
                  />
                </label>
              </div>

              {inventoryMutationError ? (
                <p className="auth-alert" role="alert">
                  {inventoryMutationError}
                </p>
              ) : null}

              <div className="dialog-actions">
                <button
                  className="ghost-button"
                  type="button"
                  onClick={closeInventoryDialog}
                  disabled={inventoryMutationStatus !== "idle"}
                >
                  Cancel
                </button>
                <button className="primary-button" type="submit" disabled={inventoryMutationStatus !== "idle"}>
                  <Save size={18} />
                  {inventoryMutationStatus === "saving" ? "Saving..." : "Save changes"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {removingInventoryItem ? (
        <div
          className="dialog-backdrop"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) {
              closeInventoryDialog();
            }
          }}
        >
          <section
            className="inventory-dialog confirmation-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="inventory-removal-title"
            aria-describedby="inventory-removal-description"
          >
            <div className="dialog-heading">
              <div>
                <p className="eyebrow">Inventory</p>
                <h2 id="inventory-removal-title">Remove {removingInventoryItem.name}?</h2>
              </div>
              <button
                className="icon-only-button"
                type="button"
                onClick={closeInventoryDialog}
                disabled={inventoryMutationStatus !== "idle"}
                aria-label="Close removal confirmation"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
            <p id="inventory-removal-description">
              This removes the listing from active inventory and the shopper page. Existing orders and sales history
              will remain.
            </p>
            {inventoryMutationError ? (
              <p className="auth-alert" role="alert">
                {inventoryMutationError}
              </p>
            ) : null}
            <div className="dialog-actions">
              <button
                className="ghost-button"
                type="button"
                onClick={closeInventoryDialog}
                disabled={inventoryMutationStatus !== "idle"}
              >
                Cancel
              </button>
              <button
                className="primary-button remove-inventory-button"
                type="button"
                onClick={removeInventoryItem}
                disabled={inventoryMutationStatus !== "idle"}
              >
                <Trash2 size={18} />
                {inventoryMutationStatus === "deleting" ? "Removing..." : "Remove item"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function InventoryActions({
  item,
  disabled,
  onEdit,
  onRemove
}: {
  item: InventoryItem;
  disabled: boolean;
  onEdit: (item: InventoryItem) => void;
  onRemove: (item: InventoryItem) => void;
}) {
  return (
    <div className="inventory-actions">
      <button
        className="icon-only-button"
        type="button"
        onClick={() => onEdit(item)}
        disabled={disabled}
        aria-label={`Edit ${item.name}`}
        title={`Edit ${item.name}`}
      >
        <Pencil size={17} />
      </button>
      <button
        className="icon-only-button danger-button"
        type="button"
        onClick={() => onRemove(item)}
        disabled={disabled}
        aria-label={`Remove ${item.name}`}
        title={`Remove ${item.name}`}
      >
        <Trash2 size={17} />
      </button>
    </div>
  );
}

function Metric({
  icon: Icon,
  label,
  value
}: {
  icon: typeof Store;
  label: string;
  value: string;
}) {
  return (
    <div className="metric-tile">
      <Icon size={20} />
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default App;
